import { Icon, Spinner } from '@amzn/awsui-components-console';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { CenteredFullPageMessage } from '../components/primitives/CenteredFullPageMessage';
import { QueryKeys } from '../constants/queryKeys';
import type { AppSettings } from './AppSettings';
import { AppSettingsContext, AppSettingsModalStateContext } from './AppSettings';
import { useModalState } from '../hooks/useModalState';

const RETRY_ATTEMPTS = 3;

/**
 * AppSettings context.
 *
 * Used to load and provide settings to the application.
 *
 * **Testing Note**: This provider should be used directly in unit tests
 * rather than mocked. For testing, you can either:
 * 1. Mock the fetch response at the network level (recommended)
 * 2. Provide a test settings.json file in your test setup
 * 3. Use MSW (Mock Service Worker) to intercept the settings request
 *
 * The provider gracefully handles loading and error states, making it
 * safe for testing environments. Tests can verify loading states,
 * error handling, and successful data loading scenarios.
 *
 * Example usage in tests:
 * ```tsx
 * // Mock the settings endpoint response
 * beforeEach(() => {
 *   global.fetch = jest.fn(() =>
 *     Promise.resolve({
 *       json: () => Promise.resolve({ region: 'us-east-1', ... })
 *     })
 *   );
 * });
 *
 * render(
 *   <QueryClient client={queryClient}>
 *     <AppSettingsProvider>
 *       <YourComponent />
 *     </AppSettingsProvider>
 *   </QueryClient>
 * );
 * ```
 *
 * @param children Nested components
 * @returns
 */
export function AppSettingsProvider(props: { readonly children: React.ReactNode }) {
  const modalState = useModalState();
  const { data, error, isLoading, isError } = useQuery({
    queryKey: [QueryKeys.APP_SETTINGS],
    queryFn: async ({ signal }) => {
      const response = await fetch(`${globalThis.location.origin}/settings.json`, { signal });
      return (await response.json()) as AppSettings;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: RETRY_ATTEMPTS,
  });

  React.useEffect(() => {
    if (error !== null) {
      console.error(new Error('Unable to load application settings', { cause: error }));
    }
  }, [error]);

  return (
    <AppSettingsModalStateContext.Provider value={modalState}>
      <AppSettingsContext.Provider value={data ?? null}>
        {isLoading ? (
          <CenteredFullPageMessage>
            <Spinner size='large' />
            Loading application settings...
          </CenteredFullPageMessage>
        ) : isError ? (
          <CenteredFullPageMessage>
            <Icon name='close' size='large' variant='error' />
            Unable to load application settings. Perhaps you forgot to add public/settings.json during local
            development.
          </CenteredFullPageMessage>
        ) : (
          props.children
        )}
      </AppSettingsContext.Provider>
    </AppSettingsModalStateContext.Provider>
  );
}
